// HelpMenuButton - ? icon in CommandBar that opens a Guided Tours dropdown
// Lists all tours (including Welcome) with status badges and contextual highlight

import { useState, useEffect, useRef } from 'preact/hooks'
import { createPortal } from 'preact/compat'
import { useLocation } from 'wouter-preact'
import { appViewMode } from '../state/appViewMode.js'
import { ionHidden } from '../state/chatStore.js'
import { openCapabilityGallery } from '../state/capabilityGalleryState.js'
import { getAllToursForSettings, getTourIdForLocation, getTour } from './tourDefinitions.js'
import { getTourStatus, startTour, resetTour } from './tourState.js'

export default function HelpMenuButton() {
  const [isOpen, setIsOpen] = useState(false)
  const [location] = useLocation()
  const containerRef = useRef(null)
  const dropdownRef = useRef(null)
  const [pos, setPos] = useState({ top: 0, right: 0 })

  // The dropdown is portaled to <body> (see below) to escape the CommandBar's
  // stacking context (`.cmdbar` is position:sticky z-index:1000, which traps
  // the dropdown's z-index BELOW the body-portaled Ion panel at z-index:1100 —
  // so the menu opened *behind* Ion). Portaled + fixed-positioned, its own
  // z-index:50001 now applies at the top level. Position it under the trigger,
  // right-aligned, from the trigger's live viewport rect.
  useEffect(() => {
    if (!isOpen || !containerRef.current) return
    const place = () => {
      if (!containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      setPos({ top: rect.bottom + 8, right: Math.max(8, window.innerWidth - rect.right) })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [isOpen])

  // Close on click outside (same mousedown pattern as UserDropdownMenu). The
  // dropdown now lives OUTSIDE containerRef (portaled), so a click inside it
  // must not count as "outside" — check both the trigger and the dropdown.
  useEffect(() => {
    const handleClickOutside = (event) => {
      const inTrigger = containerRef.current && containerRef.current.contains(event.target)
      const inDropdown = dropdownRef.current && dropdownRef.current.contains(event.target)
      if (!inTrigger && !inDropdown) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Close on route change
  useEffect(() => {
    setIsOpen(false)
  }, [location])

  const tours = getAllToursForSettings()
  const relevantTourId = getTourIdForLocation(location, appViewMode.value)

  const handleTourClick = (tourId) => {
    setIsOpen(false)
    startTour(tourId)
  }

  // ion-intro is excluded from `tours` (getAllToursForSettings) — it's an
  // auto-triggered first-run demo, not a user-pickable tour — so its "Watch
  // again" entry lives here instead, gated the same way the rest of the Ion
  // surface is (Task 2). Unlike handleTourClick, this always resets first:
  // ion-intro's demo re-runs from scratch every time, it never resumes.
  const meetIonTour = getTour('ion-intro')
  const handleMeetIonClick = () => {
    setIsOpen(false)
    resetTour('ion-intro')
    startTour('ion-intro')
  }

  // Capability gallery entry (Part D3) — reachable at any time, not gated on
  // whether ion-intro has ever run. Sits next to "Meet Ion" (same gate:
  // !ionHidden.value) since it's the same "show me what Ion does" family.
  const handleCapabilityGalleryClick = () => {
    setIsOpen(false)
    openCapabilityGallery()
  }

  const getStatusLabel = (tourId) => {
    const status = getTourStatus(tourId)
    if (status === 'completed') return 'Done'
    if (status === 'dismissed') return 'Paused'
    return 'New'
  }

  const getStatusModifier = (tourId) => {
    const status = getTourStatus(tourId)
    return status // 'unseen' | 'dismissed' | 'completed'
  }

  return (
    <div className="help-menu-container" ref={containerRef}>
      <button
        className="cmdbar-btn help-menu-trigger"
        onClick={() => setIsOpen(!isOpen)}
        title="Guided Tours"
        aria-label="Guided Tours"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {/* Question mark in circle SVG — consistent with TourPrompt icon */}
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      </button>

      {isOpen &&
        createPortal(
          <div
            className="help-menu-dropdown help-menu-dropdown--portal"
            role="menu"
            ref={dropdownRef}
            style={{ position: 'fixed', top: `${pos.top}px`, right: `${pos.right}px` }}
          >
            <div className="help-menu-header">Guided Tours</div>

          {!ionHidden.value && meetIonTour && (
            <div className="help-menu-list help-menu-list--ion">
              <button
                className="help-menu-item"
                onClick={handleMeetIonClick}
                role="menuitem"
              >
                <div className="help-menu-item-info">
                  <span className="help-menu-item-name">{meetIonTour.name}</span>
                  <span className="help-menu-item-desc">{meetIonTour.description}</span>
                </div>
                <span
                  className={`help-menu-badge help-menu-badge--${getStatusModifier('ion-intro')}`}
                >
                  {getStatusLabel('ion-intro')}
                </span>
              </button>
              <button
                className="help-menu-item"
                onClick={handleCapabilityGalleryClick}
                role="menuitem"
              >
                <div className="help-menu-item-info">
                  <span className="help-menu-item-name">What Ion can do</span>
                  <span className="help-menu-item-desc">
                    Browse recorded examples of Ion's other capabilities
                  </span>
                </div>
              </button>
            </div>
          )}

          <div className="help-menu-list">
            {tours.map((tour) => {
              const isRelevant = tour.id === relevantTourId
              return (
                <button
                  key={tour.id}
                  className={`help-menu-item${isRelevant ? ' help-menu-item--relevant' : ''}`}
                  onClick={() => handleTourClick(tour.id)}
                  role="menuitem"
                >
                  <div className="help-menu-item-info">
                    <span className="help-menu-item-name">{tour.name}</span>
                    <span className="help-menu-item-desc">{tour.description}</span>
                  </div>
                  <span
                    className={`help-menu-badge help-menu-badge--${getStatusModifier(tour.id)}`}
                  >
                    {getStatusLabel(tour.id)}
                  </span>
                </button>
              )
            })}
            </div>
          </div>,
          document.body
        )}
    </div>
  )
}
